import {
  fetchTagsConfig,
  renderBadges,
  type Badge,
  type TagsConfig,
} from "../../snippets/ss-badges/badgesShared";

const PRODUCT_SLIDER_TABS_SELECTOR = ".js-tabs";
const CARD_BADGES_ENTITY = "ss-card-badges";
const CARD_BADGES_SELECTOR = `[data-entity="${CARD_BADGES_ENTITY}"]`;
const PRODUCT_SLIDE_SELECTOR = "swiper-slide[data-product-handle]";

const SEARCHSPRING_API_URL =
  "https://api.searchspring.net/api/search/search.json";
const SEARCHSPRING_FETCH_TIMEOUT_MS = 8000;

type SearchspringResult = {
  handle?: string;
  badges?: Badge[];
};
type SearchspringResponse = { results?: SearchspringResult[] };
type BadgesByHandle = Map<string, Badge[]>;

function fetchSearchspringJson<T>(url: string): Promise<T | null> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    SEARCHSPRING_FETCH_TIMEOUT_MS
  );

  return fetch(url, { credentials: "omit", signal: controller.signal })
    .then((response) => (response.ok ? response.json() : null))
    .catch(() => null)
    .finally(() => window.clearTimeout(timeoutId));
}

function getSiteIdFromContainer(tabsContainer: HTMLElement): string {
  return tabsContainer.dataset.siteId?.trim() ?? "";
}

function getAllHandlesInContainer(
  productSlideElements: NodeListOf<HTMLElement>
): string[] {
  const productHandles: string[] = [];

  productSlideElements.forEach((productSlideElement) => {
    const productHandle =
      productSlideElement.dataset.productHandle?.trim() ?? "";
    if (!productHandle) return;
    productHandles.push(productHandle);
  });

  return productHandles;
}

async function fetchBadgesForHandles(
  siteId: string,
  handles: string[]
): Promise<BadgesByHandle> {
  if (!handles.length) return new Map();

  const queryParams = new URLSearchParams();
  queryParams.set("siteId", siteId);
  queryParams.set("resultsFormat", "native");
  queryParams.set("resultsPerPage", String(handles.length));

  const handleFilterParam = "filter.handle";
  handles.forEach((productHandle) =>
    queryParams.append(handleFilterParam, productHandle)
  );

  const requestUrl = `${SEARCHSPRING_API_URL}?${queryParams.toString()}`;

  const responseData =
    await fetchSearchspringJson<SearchspringResponse>(requestUrl);
  const searchResults = responseData?.results ?? [];

  const badgesByProductHandle = new Map<string, Badge[]>();
  searchResults.forEach((result) => {
    const productHandle =
      typeof result.handle === "string" ? result.handle : "";
    if (!productHandle || badgesByProductHandle.has(productHandle)) return;
    badgesByProductHandle.set(
      productHandle,
      Array.isArray(result.badges) ? result.badges : []
    );
  });

  return badgesByProductHandle;
}

function getOrCreateBadgesRoot(parent: HTMLElement): HTMLElement {
  const existingBadgesRoot = parent.querySelector(CARD_BADGES_SELECTOR);
  if (existingBadgesRoot instanceof HTMLElement) return existingBadgesRoot;

  const badgesRootElement = document.createElement("div");
  badgesRootElement.dataset.entity = CARD_BADGES_ENTITY;
  badgesRootElement.className = "ss__pdp-badges";
  badgesRootElement.setAttribute("aria-live", "polite");
  parent.insertBefore(badgesRootElement, parent.firstChild);
  return badgesRootElement;
}

function applyBadgesToContainer(
  productSlideElements: NodeListOf<HTMLElement>,
  badgesByHandle: BadgesByHandle,
  tagsConfig: TagsConfig | null
): void {
  productSlideElements.forEach((productSlideElement) => {
    const productHandle =
      productSlideElement.dataset.productHandle?.trim() ?? "";
    if (!productHandle) return;

    const productBadges = badgesByHandle.get(productHandle) ?? [];

    const cardElement =
      productSlideElement.querySelector<HTMLElement>(".card__inner, .card") ??
      productSlideElement;
    if (!productBadges.length) {
      cardElement.querySelector(CARD_BADGES_SELECTOR)?.remove();
      return;
    }

    const badgesRootElement = getOrCreateBadgesRoot(cardElement);
    badgesRootElement.replaceChildren();
    renderBadges(badgesRootElement, productBadges, tagsConfig);
  });
}

async function runForContainer(tabsContainer: HTMLElement): Promise<void> {
  const searchspringSiteId = getSiteIdFromContainer(tabsContainer);
  if (!searchspringSiteId) return;

  const productSlideElements = tabsContainer.querySelectorAll<HTMLElement>(
    PRODUCT_SLIDE_SELECTOR
  );
  if (!productSlideElements.length) return;

  const productHandles = getAllHandlesInContainer(productSlideElements);
  if (!productHandles.length) return;

  const [badgesByHandle, tagsConfig] = await Promise.all([
    fetchBadgesForHandles(searchspringSiteId, productHandles),
    fetchTagsConfig(searchspringSiteId, fetchSearchspringJson),
  ]);

  applyBadgesToContainer(productSlideElements, badgesByHandle, tagsConfig);
}

function mountIn(root: ParentNode): void {
  root.querySelectorAll(PRODUCT_SLIDER_TABS_SELECTOR).forEach((container) => {
    if (!(container instanceof HTMLElement)) return;
    void runForContainer(container);
  });
}

mountIn(document);
